import React from "react";
import { useTranslation } from "react-i18next";
import { GigsRoleOverviewFragment, Gig } from "gql-gen";
import { ICell } from "modules/Connection/types";
import Text from "components/Text";
import icon from "assets/info-circle.svg";
import fragment from "./role.gql";
import Money from "components/Money";
import styles from "./styles.scss";
import { DetailCell } from "components/DetailCell";
import { ReportOverviewCell, EditReportLinks } from "../ReportOverview";
import { useMobileQuery } from "components/Layout/Responsive";
import cx from "classnames";
import { isNumber } from "lodash";
import _ from "lodash";
import StatsBoard from "components/StatsBoard";
import StatsItem, { StatItemFormat } from "components/StatsBoard/StatsItem";
import variables from "styles/variables";
import { isHayday } from "utilities/flavor";

const i18n = `gigs.cells.roleOverview`;

interface HighLevelStats {
  interactions: number | null;
  samples: number | null;
  unitsSold: number | null;
}

function HighLevelStatsBoard({ data, isMobile }: { data: HighLevelStats; isMobile: boolean }) {
  const { interactions, samples, unitsSold } = data;

  // Don't render if all stats are null
  if (interactions === null && samples === null && unitsSold === null) {
    return null;
  }

  const ratio: number | undefined =
    unitsSold && samples
      ? parseFloat(((unitsSold * 100) / samples).toFixed(1))
      : undefined;

  interface StatItem {
    value?: number;
    name: string;
    transformVertically?: boolean;
    className?: string;
    format?: StatItemFormat;
  }

  const className = isMobile ? cx(styles.customItemBorder) : "";

  const statsItems = ([
    { value: interactions ?? undefined, name: "Interactions", className },
    { value: samples ?? undefined, name: "Samples", className },
    { value: unitsSold ?? undefined, name: "Sold", transformVertically: isMobile, className },
    { value: ratio, name: "Sold : Sampled", transformVertically: isMobile, className, format: "percentage" as StatItemFormat },
  ] as StatItem[]).map((statItem: StatItem) => (
    <StatsItem
      key={statItem.name}
      bgColor={variables.gray1}
      textColor={variables.teal1}
      titleColor={variables.white}
      {...statItem}
    />
  ));

  if (isMobile) {
    const [firstRow, secondRow] = _.chunk(statsItems, 2);
    return (
      <div style={{ marginBottom: '10px' }}>
        <StatsBoard loading={false} size={"medium"}>
          {firstRow}
        </StatsBoard>
        <StatsBoard loading={false} size={"medium"}>
          {secondRow}
        </StatsBoard>
      </div>
    );
  }

  return (
    <StatsBoard loading={false} size={"medium"}>
      {statsItems}
    </StatsBoard>
  );
}

export const RoleOverviewCell: ICell<GigsRoleOverviewFragment> = {
  component: props => {
    const { t } = useTranslation();
    const { data } = props;
    const isMobile = useMobileQuery();

    if (!data) {
      return null;
    }

    const programRole = data.programRole;
    const myRate = data.myRate;

    const uniform = programRole?.uniform;
    const duties = programRole?.duties;
    const profile = programRole?.profile;

    const hasRoleDetails = isNumber(myRate) || duties || uniform?.key || profile;

    // Get stats data
    const gigData = data as any;
    const hasStatsBoard = !isHayday && (gigData.interactions !== null || gigData.samples !== null || gigData.unitsSold !== null);

    return (
      <>
        {!isHayday && (
          <HighLevelStatsBoard
            data={{
              interactions: gigData.interactions,
              samples: gigData.samples,
              unitsSold: gigData.unitsSold
            }}
            isMobile={isMobile}
          />
        )}

        {/* Edit links */}
        <div className={cx(styles.editLinks, hasStatsBoard)}>
          <EditReportLinks gig={data as Gig} />
        </div>

        {/* Role details section */}
        {hasRoleDetails && (
          <div className={styles.roleOverview}>
            {myRate ? (
              <DetailCell title={t(`${i18n}.rate.title`)}>
                <Text>
                  <Money value={myRate} decimalSeparator="." />
                </Text>
              </DetailCell>
            ) : (
              ""
            )}

            {duties && duties.trim() ? (
              <DetailCell key="duties" title={t(`${i18n}.responsibilities.title`)}>
                <Text>{duties}</Text>
              </DetailCell>
            ) : (
              ""
            )}

            {uniform && uniform.key ? (
              <DetailCell key="uniform" title={t(`${i18n}.attire.title`)}>
                <>
                  <Text bold size={12} bottom="xs">
                    {uniform.label}
                  </Text>
                  <Text>{uniform.description}</Text>
                </>
              </DetailCell>
            ) : (
              ""
            )}

            {profile && profile.trim() ? (
              <DetailCell key="profile" title={t(`${i18n}.profile.title`)}>
                <Text>{profile}</Text>
              </DetailCell>
            ) : (
              ""
            )}
          </div>
        )}

        {/* Report section */}
        <ReportOverviewCell.component {...(props as any)} />
      </>
    );
  },
  icon: icon,
  fragment: fragment,
};
